import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@mui/material';
import { ConversationStatus } from 'shared';

import { Badge } from '../../../../common/Atoms/Badge';
import { Paragraph } from '../../../../common/Atoms/Typography/Paragraph';
import { classNames } from '../../../../common/lib/classNames';
import { AgentConversation } from '../../../services/api/aiAgentApi/aiAgent';
import { getConversationStatusMeta, parseConversationSummary } from '../lib/agentDisplay';

import { conversationHasFailure, formatLocalTimestamp } from './conversationHelpers';
import { ContactAvatar } from './ContactAvatar';

export interface ConversationListItemProps {
  agentConversation: AgentConversation;
  selected: boolean;
  onSelect: (conversationId: string) => void;
  onResolve: (conversationId: string) => void;
  isResolving: boolean;
}

export function ConversationListItem({
  agentConversation,
  selected,
  onSelect,
  onResolve,
  isResolving,
}: ConversationListItemProps) {
  const statusMeta = getConversationStatusMeta(agentConversation.status);
  const pendingCount = agentConversation.pending_task_count || 0;
  const failed = conversationHasFailure(agentConversation);
  const parsedSummary = parseConversationSummary(agentConversation.summary);
  const isTerminal = [ConversationStatus.RESOLVED, ConversationStatus.CLOSED].includes(agentConversation.status);
  const canResolveQuickly = !isTerminal && !agentConversation.archived_at;

  return (
    <div
      role={ `button` }
      tabIndex={ 0 }
      className={ classNames(
        `w-full rounded-lg border px-3.5 py-3 text-left transition-colors`,
        selected
          ? `border-paidniceGreen-300 bg-paidniceGreen-50 shadow-xs`
          : `border-transparent hover:border-gray-200 hover:bg-gray-50`,
      ) }
      onClick={ () => onSelect(agentConversation.id) }
      onKeyDown={ event => {
        if (event.target !== event.currentTarget) {
          return;
        }

        if (event.key === `Enter` || event.key === ` `) {
          event.preventDefault();
          onSelect(agentConversation.id);
        }
      } }
    >
      <div className={ `flex items-start gap-3` }>
        <ContactAvatar
          name={ agentConversation.contact_name }
          alert={ pendingCount > 0 || failed }
        />
        <div className={ `min-w-0 flex-1 flex items-start justify-between gap-2` }>
          <div className={ `min-w-0 flex-1` }>
            <div className={ `flex items-start justify-between gap-2` }>
              <Paragraph
                size={ `sm` }
                className={ `truncate` }
              >
                { agentConversation.contact_name || `Unknown contact` }
              </Paragraph>
              <Badge color={ statusMeta.color }>
                { statusMeta.label }
              </Badge>
            </div>
            <div className={ `mt-1 flex items-start justify-between gap-2` }>
              <Paragraph
                size={ `xs` }
                variant={ `secondary` }
                className={ `line-clamp-2` }
              >
                { parsedSummary.summary || `No summary yet` }
              </Paragraph>
              {
                canResolveQuickly &&
                <Tooltip title={ `Mark as resolved` }>
                  <button
                    type={ `button` }
                    aria-label={ `Mark conversation as resolved` }
                    className={ `mt-0.5 shrink-0 rounded-full border border-gray-200 p-1 text-gray-400 transition hover:border-paidniceGreen-300 hover:bg-paidniceGreen-50 hover:text-paidniceGreen-600 disabled:cursor-not-allowed disabled:opacity-50` }
                    disabled={ isResolving }
                    onClick={ event => {
                      event.stopPropagation();
                      onResolve(agentConversation.id);
                    } }
                  >
                    <CheckCircleIcon className={ `h-4 w-4` } />
                  </button>
                </Tooltip>
              }
            </div>
            <div className={ `mt-2 flex items-center justify-between gap-2` }>
              <span className={ `text-xs text-gray-400` }>
                { formatLocalTimestamp(agentConversation.last_activity_at) }
              </span>
              <div className={ `flex items-center gap-1` }>
                {
                  pendingCount > 0 &&
                  <Badge color={ `orange` }>
                    { `${pendingCount} to approve` }
                  </Badge>
                }
                {
                  failed &&
                  <Badge color={ `red` }>
                    { `Needs attention` }
                  </Badge>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
